import { ChevronDown, ChevronLeft, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useParams } from 'react-router-dom';
import { useInitialKeys } from '../../hooks/useInitialKeys';
import { useQuestionnaireDetails } from '../../hooks/useQuestionnaireDetails';
import { useQuestionTypes } from '../../hooks/useQuestionTypes';
import { useUpdateQuestionnaire } from '../../hooks/useUpdateQuestionnaire';

import {
  AddQuestionButton,
  BackButton,
  Button,
  Checkbox,
  Container,
  ContentWrapper,
  FormContainer,
  FormSection,
  Header,
  HeaderTitle,
  Input,
  QuestionItem,
  QuestionsContainer,
  Select,
  SelectArrow,
  SelectContainer,
  Textarea,
  Title,
} from './EditQuestionnairePage.Styles';

/**
 * @module EditQuestionnairePage
 * @description Component for editing existing questionnaires in the Mental Health Portal application.
 * Allows users to modify questionnaire metadata, add/remove questions, and update question-specific settings.
 *
 * @requires react
 * @requires react-i18next
 * @requires react-oidc-context
 * @requires react-router-dom
 * @requires lucide-react
 * @requires ../../hooks/useInitialKeys
 * @requires ../../hooks/useQuestionnaireDetails
 * @requires ../../hooks/useQuestionTypes
 * @requires ../../hooks/useUpdateQuestionnaire
 * @requires ./EditQuestionnairePage.Styles
 */
export default function EditQuestionnairePage() {
  /** Translation hook for internationalization */
  const { t } = useTranslation();
  /** URL parameter for questionnaire ID */
  const { id } = useParams();
  /** Navigation hook for routing */
  const navigate = useNavigate();

  /**
   * Custom hook for fetching questionnaire details
   * @type {Object}
   * @property {Object} questionnaire - The questionnaire data
   * @property {boolean} loadingQuestionnaire - Loading state for questionnaire data
   */
  const { questionnaire, loading: loadingQuestionnaire } =
    useQuestionnaireDetails(id);

  /**
   * Custom hook for fetching question types
   * @type {Object}
   * @property {Array} questionTypes - Available question types
   * @property {boolean} loadingTypes - Loading state for question types
   */
  const { questionTypes, loading: loadingTypes } = useQuestionTypes();

  /**
   * Custom hook for updating questionnaire
   * @type {Object}
   * @property {Function} updateQuestionnaire - Function to update questionnaire
   * @property {boolean} updating - Loading state during update
   * @property {Object} error - Error object if update fails
   */
  const {
    updateQuestionnaire,
    loading: updating,
    error,
  } = useUpdateQuestionnaire();

  /** @state {string} name - Questionnaire name */
  const [name, setName] = useState('');
  /** @state {string} toBeAppliedAt - When the questionnaire should be applied (initial, daily, weekly) */
  const [toBeAppliedAt, setToBeAppliedAt] = useState('');
  /** @state {string} category - Questionnaire category (personality, mood, anxiety, etc.) */
  const [category, setCategory] = useState('');
  /** @state {string} correctionType - Algorithm used for scoring (Big Five, Oldenburg, Balance Scale) */
  const [correctionType, setCorrectionType] = useState('');
  /** @state {string} introduction - Introductory text for the questionnaire */
  const [introduction, setIntroduction] = useState('');
  /** @state {string} questionTypeId - Selected question type ID */
  const [questionTypeId, setQuestionTypeId] = useState('');
  /** @state {Array<string>} questions - List of question texts */
  const [questions, setQuestions] = useState([]);
  /** @state {Array<boolean>} selectedQuestions - Boolean flags for inverting answers for each question */
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  /** @state {Array<number>} keyIds - List of key IDs associated with each question */
  const [keyIds, setKeyIds] = useState([]);
  /** Authentication hook for user management */
  const auth = useAuth();

  /** User ID from authentication */
  const userId = auth.user?.profile?.sub;

  /**
   * @const {Object} friendlyLabels - Human-readable labels for question types
   * @property {string} '1-5' - Scale of 1 to 5
   * @property {string} '0-6' - Scale of 0 to 6
   * @property {string} 'yes-or-no' - Yes or No options
   */
  const friendlyLabels = {
    '1-5': `${t('Scale of 1 to 5')}`,
    '0-6': `${t('Scale of 0 to 6')}`,
    'yes-or-no': `${t('Yes or No')}`,
  };

  /**
   * Custom hook for fetching available keys for question categorization
   * @type {Object}
   * @property {Array} initialKeys - Available keys
   * @property {boolean} loadingKeys - Loading state for keys
   */
  const { initialKeys, loading: loadingKeys } = useInitialKeys();

  /**
   * Effect hook to populate form with questionnaire data when it's loaded
   * @effect
   * @param {Object} questionnaire - The questionnaire data from API
   * @returns {void}
   */
  useEffect(() => {
    if (questionnaire) {
      // Set basic questionnaire metadata
      setName(questionnaire.name || '');
      setToBeAppliedAt(questionnaire.to_be_applied_at || '');
      setCategory(questionnaire.category || '');
      setCorrectionType(questionnaire.correction_type || '');
      setIntroduction(questionnaire.introduction || '');

      // Transform API question format to component state format
      const existing = questionnaire.questions?.length
        ? questionnaire.questions.map((q) => ({
            id: q.id,
            question: q.question,
            invertAnswer: q.invert_answer,
            questionTypeId: q.question_type_id,
            keyId: q.key_id,
          }))
        : [];

      // Update state with existing questions data
      setQuestions(existing.map((q) => q.question));
      setSelectedQuestions(existing.map((q) => q.invertAnswer));
      setKeyIds(existing.map((q) => q.keyId));
      setQuestionTypeId(existing[0]?.questionTypeId || '');
    }
  }, [questionnaire]);

  /**
   * Navigates back to the questionnaires list page
   * @function handleBack
   * @returns {void}
   */
  const handleBack = () => navigate('/questionnaires');

  /**
   * Updates the text of a specific question
   * @function handleQuestionChange
   * @param {number} index - Index of the question to update
   * @param {string} value - New question text
   * @returns {void}
   */
  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  /**
   * Updates the key ID for a specific question
   * @function handleKeyChange
   * @param {number} index - Index of the question to update
   * @param {string|number} value - New key ID
   * @returns {void}
   */
  const handleKeyChange = (index, value) => {
    const newKeyIds = [...keyIds];
    newKeyIds[index] = Number(value);
    setKeyIds(newKeyIds);
  };

  /**
   * Toggles the "invert answer" flag for a question
   * @function toggleQuestion
   * @param {number} index - Index of the question to toggle
   * @returns {void}
   */
  const toggleQuestion = (index) => {
    const newSelected = [...selectedQuestions];
    newSelected[index] = !newSelected[index];
    setSelectedQuestions(newSelected);
  };

  /**
   * Adds a new empty question to the list with default values
   * @function addQuestion
   * @returns {void}
   */
  const addQuestion = () => {
    setQuestions([...questions, '']);
    setSelectedQuestions([...selectedQuestions, false]);
    setKeyIds([...keyIds, 1]);
  };

  /**
   * Removes a question from the list
   * @function removeQuestion
   * @param {number} index - Index of the question to remove
   * @returns {void}
   */
  const removeQuestion = (index) => {
    const newQuestions = [...questions];
    const newSelected = [...selectedQuestions];
    const newKeyIds = [...keyIds];

    newQuestions.splice(index, 1);
    newSelected.splice(index, 1);
    newKeyIds.splice(index, 1);

    setQuestions(newQuestions);
    setSelectedQuestions(newSelected);
    setKeyIds(newKeyIds);
  };

  /**
   * Validates form data and submits the updated questionnaire
   * @function handleSave
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If questionnaire update fails
   */
  const handleSave = async () => {
    /**
     * @typedef {Object} QuestionPayload
     * @property {string} text - Question text
     * @property {boolean} invertAnswer - Whether to invert the answer scoring
     * @property {number} questionTypeId - Type of question (scale, yes/no, etc.)
     * @property {number} keyId - Key associated with the question
     */

    // Format questions for API payload
    const updatedQuestions = questions.map((question, index) => ({
      text: typeof question === 'string' ? question : question.question,
      invertAnswer: selectedQuestions[index],
      questionTypeId: parseInt(questionTypeId),
      keyId: keyIds[index],
    }));

    /**
     * @typedef {Object} QuestionnaireUpdatePayload
     * @property {string} name - Questionnaire name
     * @property {string} toBeAppliedAt - When the questionnaire should be applied
     * @property {string} category - Questionnaire category
     * @property {string} correctionType - Algorithm used for scoring
     * @property {string} introduction - Introductory text
     * @property {string} updatedBy - User ID of updater
     * @property {Array<QuestionPayload>} questions - List of questions
     */

    /** @type {QuestionnaireUpdatePayload} */
    const payload = {
      name,
      toBeAppliedAt,
      category,
      correctionType,
      introduction,
      updatedBy: userId,
      questions: updatedQuestions,
    };

    // Submit questionnaire update and handle response
    const result = await updateQuestionnaire(id, payload);

    if (result) {
      navigate('/questionnaires');
    } else {
      alert(`${t('Error updating questionnaire')}`);
    }
  };

  /**
   * Render loading state while fetching questionnaire data
   */
  if (loadingQuestionnaire) return <p>{t('Loading questionnaires')}</p>;

  return (
    <Container>
      <Header>
        <HeaderTitle>
          <BackButton onClick={handleBack}>
            <ChevronLeft size={20} />
          </BackButton>
          <Title>{t('Edit Questionnaire')}</Title>
        </HeaderTitle>
        <Button onClick={handleSave} disabled={updating}>
          {updating ? `${t('Saving')}` : `${t('Save')}`}
        </Button>
      </Header>

      <ContentWrapper>
        <FormContainer>
          <FormSection>
            <Input
              placeholder={t('Questionnaire name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <SelectContainer>
              <Select
                value={toBeAppliedAt}
                onChange={(e) => setToBeAppliedAt(e.target.value)}
              >
                <option value="" disabled>
                  {t('Time to be applied')}
                </option>
                <option value="inicial">{t('Initial')}</option>
                <option value="diario">{t('Daily')}</option>
                <option value="semanal">{t('Weekly')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="" disabled>
                  {t('Category')}
                </option>
                <option value="personalidade">{t('Personality')}</option>
                <option value="humor">{t('Mood')}</option>
                <option value="ansiedade">{t('Anxiety')}</option>
                <option value="depressao">{t('Depression')}</option>
                <option value="burnout">{t('Burnout')}</option>
                <option value="outros">{t('Other')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={questionTypeId}
                onChange={(e) => setQuestionTypeId(e.target.value)}
              >
                <option value="" disabled>
                  {t('Questions type')}
                </option>
                {loadingTypes && <option disabled>{t('Loading')}</option>}
                {error && <option disabled>{t('Error loading types')}</option>}
                {questionTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {friendlyLabels[type.identifier] || type.identifier}
                  </option>
                ))}
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <SelectContainer>
              <Select
                value={correctionType}
                onChange={(e) => setCorrectionType(e.target.value)}
              >
                <option value="" disabled>
                  {t('Correction type')}
                </option>
                <option value="bigFive">{t('Big Five')}</option>
                <option value="oldenburg">{t('Oldenburg')}</option>
                <option value="equilibrio">{t('Balance Scale')}</option>
              </Select>
              <SelectArrow>
                <ChevronDown size={16} />
              </SelectArrow>
            </SelectContainer>

            <Textarea
              placeholder={t('Initial explanation')}
              value={introduction}
              onChange={(e) => setIntroduction(e.target.value)}
            />
          </FormSection>

          <FormSection>
            <QuestionsContainer>
              {questions.map((question, index) => (
                <QuestionItem key={index}>
                  <Input
                    placeholder={`${t('Question')} ${index + 1}`}
                    value={question}
                    onChange={(e) =>
                      handleQuestionChange(index, e.target.value)
                    }
                  />

                  <SelectContainer>
                    <Select
                      value={keyIds[index]}
                      onChange={(e) => handleKeyChange(index, e.target.value)}
                    >
                      {loadingKeys ? (
                        <option disabled>{t('Loading')}</option>
                      ) : (
                        initialKeys.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.name}
                          </option>
                        ))
                      )}
                    </Select>
                    <SelectArrow>
                      <ChevronDown size={16} />
                    </SelectArrow>
                  </SelectContainer>

                  <Checkbox
                    isChecked={selectedQuestions[index]}
                    onClick={() => toggleQuestion(index)}
                  >
                    {selectedQuestions[index] && '✓'}
                  </Checkbox>

                  {questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        color: '#e63946',
                      }}
                      title={t('Remove question')}
                    >
                      <X size={16} />
                    </button>
                  )}
                </QuestionItem>
              ))}

              <AddQuestionButton onClick={addQuestion}>
                <Plus size={16} />
              </AddQuestionButton>
            </QuestionsContainer>
          </FormSection>
        </FormContainer>
      </ContentWrapper>
    </Container>
  );
}
